import { Injectable } from '@angular/core';
import { objectToCamel, objectToSnake } from 'ts-case-convert';
import { lastValueFrom } from 'rxjs';
import { ClientHttpService } from './client.http.service';
import {
  IClientConfig,
  IBase,
  IClientDetail,
  IClientInfo,
  IClientInfoGroup,
  IClientInfoGroupSavePayload,
  IClientPaymentConfig,
  IClientPaymentConfigUpdate,
  IClientSavePayload,
  IClientSummary,
  IListQuery,
  IPageResponse,
  IPaginatedList,
} from 'src/app/models';
import { ClientType } from 'src/app/models/enums';
import { UtilsService } from '../utils/utils.service';

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  constructor(
    private clientHttpService: ClientHttpService,
    private utilsService: UtilsService,
  ) {}

  async getServices(clientId: number) {
      
    const source$ = this.clientHttpService.getServiceInfos(clientId);
    const response = await lastValueFrom(source$);
    const services = objectToCamel(response as object);
    return this.getData(services);
  }

  async getPaymentConfig(clientId: number): Promise<IClientPaymentConfig> {
    const response = await lastValueFrom(this.clientHttpService.getPaymentConfig(clientId));
    return objectToCamel(response as object) as IClientPaymentConfig;
  }

  async updatePaymentConfig(clientId: number, paymentConfig: IClientPaymentConfigUpdate): Promise<IClientPaymentConfig> {
    const response = await lastValueFrom(
      this.clientHttpService.updatePaymentConfig(clientId, objectToSnake(paymentConfig)),
    );
    return objectToCamel(response as object) as IClientPaymentConfig;
  }

  async searchClients(query: IListQuery, clientTypes: ClientType[] = []): Promise<IPaginatedList<IClientSummary>> {
    const queryParams = this.utilsService.toQueryParams({ ...query, clientType: clientTypes });
    const response = await lastValueFrom(this.clientHttpService.searchClients(queryParams));
    const clients = objectToCamel(response as object) as { data: IPageResponse<IClientSummary> };
    return this.utilsService.getDataWithPagination(clients.data, query.page);
  }

  async getClient(clientId: number): Promise<IClientDetail> {
    const response = await lastValueFrom(this.clientHttpService.getClient(clientId));
    return objectToCamel(response as object) as IClientDetail;
  }

  async saveClient(client: IClientSavePayload, clientId?: number): Promise<IClientDetail> {
    const body = objectToSnake(client);
    const source$ = clientId
      ? this.clientHttpService.updateClient(clientId, body)
      : this.clientHttpService.createClient(body);
    const response = await lastValueFrom(source$);
    return objectToCamel(response as object) as IClientDetail;
  }

  async setClientActive(clientId: number, active: boolean) {
    const source$ = active
      ? this.clientHttpService.reactivateClient(clientId)
      : this.clientHttpService.deactivateClient(clientId);
    await lastValueFrom(source$);
  }

  async getClientConfig(clientId: number): Promise<IClientConfig> {
    const response = await lastValueFrom(this.clientHttpService.getClientConfig(clientId));
    return objectToCamel(response as object) as IClientConfig;
  }

  async updateClientConfig(clientId: number, clientConfig: IClientConfig): Promise<IClientConfig> {
    const response = await lastValueFrom(
      this.clientHttpService.updateClientConfig(clientId, objectToSnake(clientConfig)),
    );
    return objectToCamel(response as object) as IClientConfig;
  }

  async saveClientInfo(clientId: number, clientInfo: IClientInfo): Promise<IClientInfo> {
    const response = await lastValueFrom(
      this.clientHttpService.saveClientInfo(clientId, objectToSnake(clientInfo)),
    );
    return objectToCamel(response as object) as IClientInfo;
  }

  async getClientInfoGroups(clientId: number): Promise<IClientInfoGroup[]> {
    const response = await lastValueFrom(this.clientHttpService.getClientInfoGroups(clientId));
    return objectToCamel(response as object) as IClientInfoGroup[];
  }

  async saveClientInfoGroup(
    clientId: number,
    clientInfoGroup: IClientInfoGroupSavePayload,
    clientInfoId?: number,
  ): Promise<IClientInfoGroup> {
    const body = objectToSnake(clientInfoGroup);
    const source$ = clientInfoId
      ? this.clientHttpService.updateClientInfoGroup(clientId, clientInfoId, body)
      : this.clientHttpService.createClientInfoGroup(clientId, body);
    return objectToCamel((await lastValueFrom(source$)) as object) as IClientInfoGroup;
  }

  async deleteClientInfoGroup(clientId: number, clientInfoId: number): Promise<void> {
    await lastValueFrom(this.clientHttpService.deleteClientInfoGroup(clientId, clientInfoId));
  }

  async searchClientTowns(clientId: number, query: IListQuery): Promise<IPaginatedList<IBase>> {
    const queryParams = this.utilsService.toQueryParams(query);
    const response = await lastValueFrom(this.clientHttpService.searchClientTowns(clientId, queryParams));
    const towns = objectToCamel(response as object) as { data: IPageResponse<IBase> };
    return this.utilsService.getDataWithPagination(towns.data, query.page);
  }

  getData(response: any) {
    return response.data;
  }
}
